// pages/VehicleTracking.jsx
import React, { useState, useMemo } from 'react';
import { useVehicleTracking } from '../../hooks/useVehicleTracking';
import { useTrackingProviders } from '../../context/TrackingProvidersContext';
import { useLayout } from '../../context/LayoutContext';
import VehicleMap from '../../components/tracking/VehicleMap';
import VehicleList from '../../components/tracking/VehicleList';
import VehicleDetails from '../../components/tracking/VehicleDetails';
import EventLog from '../../components/tracking/EventLog';
import '../../assets/scss/views/VehicleTracking.scss';

// Iconos SVG importados
import { ReactComponent as RefreshIcon } from '../../assets/svg/view/refresh.svg';
import { ReactComponent as MapIcon } from '../../assets/svg/layout/travel.svg';
import { ReactComponent as ListIcon } from '../../assets/svg/view/list.svg';
import { ReactComponent as SearchIcon } from '../../assets/svg/components/search.svg';
import { ReactComponent as SortIcon } from '../../assets/svg/view/sort.svg';

// Definición de constantes para filtros
const VEHICLE_FILTERS = {
  ALL: 'all',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SPEED_60: 'speed_60',
  SPEED_80: 'speed_80',
  INACTIVE_3_DAYS: 'inactive_3_days'
};

const FILTER_LABELS = {
  [VEHICLE_FILTERS.ALL]: 'Todos los vehículos',
  [VEHICLE_FILTERS.ACTIVE]: 'Vehículos activos',
  [VEHICLE_FILTERS.INACTIVE]: 'Vehículos inactivos',
  [VEHICLE_FILTERS.SPEED_60]: 'Más de 60 km/h',
  [VEHICLE_FILTERS.SPEED_80]: 'Más de 80 km/h',
  [VEHICLE_FILTERS.INACTIVE_3_DAYS]: 'Inactivos más de 3 días'
};

const VehicleTracking = () => {
  const { theme } = useLayout();
  const { providers, activeProvider, changeProvider } = useTrackingProviders();
  
  // 🔄 NUEVO: Hook actualizado con suscripción en tiempo real
  const { 
    vehicles, 
    isLoading, 
    isError, 
    error, 
    selectedVehicle, 
    selectVehicle, 
    clearSelection,
    forceUpdate,
    trackedEvents,
    isConnected,
    connectionStatus,
    lastUpdate,
    providerStats,
    loadingHistory
  } = useVehicleTracking(activeProvider.id);
  
  const [viewMode, setViewMode] = useState('map'); // 'map' o 'list'
  const [sidebarContent, setSidebarContent] = useState('details'); // 'details' o 'events'
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' o 'desc'
  const [vehicleFilter, setVehicleFilter] = useState(VEHICLE_FILTERS.ALL);

  const isVehicleActive = (vehicle) => {
    return (vehicle.GpsSpeed || 0) > 0 || vehicle.Ignition === 1;
  };

  const isInactiveForDays = (vehicle, days) => {
    if (!vehicle.ReportDate) return false;
    const lastReport = new Date(vehicle.ReportDate);
    const now = new Date();
    const diffDays = (now - lastReport) / (1000 * 60 * 60 * 24);
    return diffDays >= days;
  };
  
  // Filtrar y ordenar vehículos
  const filteredVehicles = useMemo(() => {
    let filtered = [...vehicles];
    
    // Aplicar filtro de estado
    switch (vehicleFilter) {
      case VEHICLE_FILTERS.ACTIVE:
        filtered = filtered.filter(isVehicleActive);
        break;
      case VEHICLE_FILTERS.INACTIVE:
        filtered = filtered.filter(v => !isVehicleActive(v));
        break;
      case VEHICLE_FILTERS.SPEED_60:
        filtered = filtered.filter(v => (v.GpsSpeed || 0) > 60);
        break;
      case VEHICLE_FILTERS.SPEED_80:
        filtered = filtered.filter(v => (v.GpsSpeed || 0) > 80);
        break;
      case VEHICLE_FILTERS.INACTIVE_3_DAYS:
        filtered = filtered.filter(v => isInactiveForDays(v, 3));
        break;
      default:
        // ALL: mantener todos los vehiculos
        break;
    }
    
    // Aplicar filtro de búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      
      filtered = filtered.filter(v => {
        // Campos comunes para búsqueda
        const unitId = (v.UnitId || '').toLowerCase();
        const unitPlate = (v.UnitPlate || '').toLowerCase();
        const domicilio = (v.Domicilio || '').toLowerCase();
        
        // Campos adicionales según proveedor
        const imei = (v.Imei || '').toLowerCase();
        const placa = (v.Placa || '').toLowerCase();
        const gpsIdentif = (v.GpsIdentif || '').toLowerCase();
        
        // Búsqueda en todos los campos relevantes
        return unitId.includes(term) || 
               unitPlate.includes(term) || 
               domicilio.includes(term) ||
               imei.includes(term) ||
               placa.includes(term) ||
               gpsIdentif.includes(term);
      });
    }
    
    // Ordenar alfabéticamente
    filtered.sort((a, b) => {
      // Dar prioridad a UnitId, luego UnitPlate
      const nameA = (a.UnitId || a.UnitPlate || '').toLowerCase();
      const nameB = (b.UnitId || b.UnitPlate || '').toLowerCase();
      
      if (sortOrder === 'asc') {
        return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
      } else {
        return nameB.localeCompare(nameA, 'es', { sensitivity: 'base' });
      }
    });
    
    return filtered;
  }, [vehicles, searchTerm, sortOrder, vehicleFilter]);
  
  // Verificar si hay vehículos con datos válidos para el mapa
  const validVehicles = useMemo(() => {
    return filteredVehicles.filter(v => 
      v && v.Latitude && v.Longitude && 
      !isNaN(parseFloat(v.Latitude)) && !isNaN(parseFloat(v.Longitude))
    );
  }, [filteredVehicles]);
  
  // Manejar cambio de proveedor
  const handleProviderChange = (event) => {
    changeProvider(event.target.value);
  };
  
  // Manejar cambio de pestaña en sidebar
  const handleSidebarTabChange = (tabValue) => {
    setSidebarContent(tabValue);
  };
  
  // Manejar cambio en la búsqueda
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleVehicleFilterChange = (event) => {
    setVehicleFilter(event.target.value);
  };
  
  // Manejar cambio en el orden
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // 🎨 NUEVO: Obtener estado de conexión con estilos
  const getConnectionStatusStyle = () => {
    const baseClass = `GM__${theme}-connection-status`;
    switch (connectionStatus) {
      case 'connected':
        return `${baseClass} connected`;
      case 'connecting':
        return `${baseClass} connecting`;
      case 'disconnected':
        return `${baseClass} disconnected`;
      default:
        return `${baseClass} unknown`;
    }
  };

  // 🎨 NUEVO: Obtener texto de estado de conexión
  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return isConnected ? 'En tiempo real' : 'Conectado';
      case 'connecting':
        return 'Conectando...';
      case 'disconnected':
        return 'Desconectado';
      default:
        return 'Estado desconocido';
    }
  };

  // 🎨 NUEVO: Formatear última actualización
const getLastUpdateText = () => {
  if (!lastUpdate) return 'Nunca';
  
  try {
    // Para tiempo real, usar la hora tal como viene del servidor
    const updateTime = new Date(lastUpdate + (lastUpdate.includes('Z') ? '' : 'Z'));
    const now = new Date();
    const diffSeconds = Math.floor((now - updateTime) / 1000);
    
    if (diffSeconds < 60) {
      return `Hace ${diffSeconds} segundos`;
    } else if (diffSeconds < 3600) {
      const minutes = Math.floor(diffSeconds / 60);
      return `Hace ${minutes} minuto${minutes !== 1 ? 's' : ''}`;
    } else {
      // Mostrar hora local manteniendo la hora original
      return updateTime.toLocaleTimeString('es-CO', {
        timeZone: 'America/Bogota',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  } catch (error) {
    console.error('Error formateando última actualización:', error);
    return 'Fecha inválida';
  }
};
  
  return (
    <div className={`GM__${theme}-vehicle-tracking-container`}>
      {/* Barra superior con controles */}
      <div className={`GM__${theme}-controls-section`}>
        <div className={`GM__${theme}-controls-header`}>
          <h1 className={`GM__${theme}-title`}>
            Seguimiento de Vehículos
            {activeProvider.id === 'all' && ' - Todos los Proveedores'}
          </h1>
          
          {/* 🆕 NUEVO: Indicador de estado de conexión */}
          <div className={getConnectionStatusStyle()}>
            <span className={`GM__${theme}-connection-text`}>
              {getConnectionStatusText()}
            </span>
            {lastUpdate && (
              <span className={`GM__${theme}-last-update`}>
                • {getLastUpdateText()}
              </span>
            )}
          </div>
          
          <div className={`GM__${theme}-controls-actions`}>
            <div className={`GM__${theme}-provider-selector`}>
              <select 
                value={activeProvider.id}
                onChange={handleProviderChange}
                className={`GM__${theme}-select`}
                disabled={isLoading}
              >
                {providers.map(provider => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </div>
            
            <button 
              className={`GM__${theme}-button ${isLoading ? 'loading' : ''}`}
              onClick={forceUpdate}
              disabled={isLoading || !isConnected}
              title={!isConnected ? 'Sin conexión al servidor' : 'Actualizar posiciones'}
            >
              {isLoading || loadingHistory ? (
                <div className={`GM__${theme}-loading-spinner`}></div>
              ) : (
                <RefreshIcon className={`GM__${theme}-button-icon`} />
              )}
              <span>{isLoading ? 'Actualizando...' : 'Actualizar'}</span>
            </button>
            
            <button
              className={`GM__${theme}-button`}
              onClick={() => setViewMode(prev => prev === 'map' ? 'list' : 'map')}
            >
              {viewMode === 'map' ? (
                <>
                  <ListIcon className={`GM__${theme}-button-icon`} />
                  <span>Ver Lista</span>
                </>
              ) : (
                <>
                  <MapIcon className={`GM__${theme}-button-icon`} />
                  <span>Ver Mapa</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Filtros */}
        <div className={`GM__${theme}-filters-section`}>
          <div className={`GM__${theme}-search-container`}>
            <SearchIcon className={`GM__${theme}-search-icon`} />
            <input
              type="text"
              placeholder="Buscar vehículo por ID, placa o ubicación..."
              value={searchTerm}
              onChange={handleSearchChange}
              className={`GM__${theme}-search-input-gps`}
            />
          </div>
          
          <div className={`GM__${theme}-filter-container`}>
            <select
              value={vehicleFilter}
              onChange={handleVehicleFilterChange}
              className={`GM__${theme}-select`}
            >
              {Object.entries(FILTER_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          
          <button 
            className={`GM__${theme}-sort-button`}
            onClick={toggleSortOrder}
            title={`Ordenar ${sortOrder === 'asc' ? 'Z-A' : 'A-Z'}`}
          >
            <SortIcon className={`GM__${theme}-sort-icon ${sortOrder === 'desc' ? 'rotated' : ''}`} />
            <span>Ordenar {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}</span>
          </button>
          
          <div className={`GM__${theme}-results-count`}>
            {filteredVehicles.length} vehículos encontrados
            {/* 🆕 NUEVO: Mostrar estadísticas del proveedor */}
            {providerStats && (
              <span className={`GM__${theme}-provider-stats`}>
                • {providerStats.activeVehicles} activos
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Contenido principal */}
      {isError ? (
        <div className={`GM__${theme}-error-container`}>
          <h2 className={`GM__${theme}-error-title`}>
            Error de conexión
          </h2>
          <p className={`GM__${theme}-error-message`}>
            {error?.message || 'No se puede conectar con el servidor de tracking'}
          </p>
          <div className={`GM__${theme}-error-details`}>
            <p>Estado de conexión: <strong>{connectionStatus}</strong></p>
            <p>Última actualización: <strong>{getLastUpdateText()}</strong></p>
          </div>
          <button 
            className={`GM__${theme}-button GM__${theme}-primary-button`} 
            onClick={forceUpdate}
            disabled={isLoading}
          >
            {isLoading ? 'Reintentando...' : 'Reintentar Conexión'}
          </button>
        </div>
      ) : (
        <div className={`GM__${theme}-content-grid`}>
          {/* Panel principal */}
          <div className={`GM__${theme}-main-panel`}>
            {isLoading && vehicles.length === 0 ? (
              <div className={`GM__${theme}-loading-container`}>
                <div className={`GM__${theme}-loading-spinner-large`}></div>
                <p className={`GM__${theme}-loading-text`}>
                  {connectionStatus === 'connecting' 
                    ? 'Conectando con el servidor...' 
                    : 'Cargando vehículos...'}
                </p>
                <p className={`GM__${theme}-loading-subtext`}>
                  Esto puede tomar unos momentos la primera vez
                </p>
              </div>
            ) : (
              <>
                {/* 🆕 NUEVO: Banner de información si no hay conexión */}
                {!isConnected && vehicles.length > 0 && (
                  <div className={`GM__${theme}-warning-banner`}>
                    <span className={`GM__${theme}-warning-icon`}>⚠️</span>
                    <span className={`GM__${theme}-warning-text`}>
                      Mostrando datos en caché. La conexión en tiempo real se restablecerá automáticamente.
                    </span>
                  </div>
                )}

                {viewMode === 'map' ? (
                  <VehicleMap 
                    vehicles={validVehicles} 
                    selectedVehicle={selectedVehicle}
                    onSelectVehicle={selectVehicle}
                    theme={theme}
                    isConnected={isConnected}
                    connectionStatus={connectionStatus}
                  />
                ) : (
                  <VehicleList 
                    vehicles={filteredVehicles} 
                    selectedVehicleId={selectedVehicle?.UnitId || selectedVehicle?.UnitPlate}
                    onSelectVehicle={selectVehicle}
                    theme={theme}
                    isConnected={isConnected}
                    connectionStatus={connectionStatus}
                  />
                )}
              </>
            )}
          </div>
          
          {/* Panel lateral derecho */}
          <div className={`GM__${theme}-sidebar-panel`}>
            <div className={`GM__${theme}-sidebar-tabs`}>
              <button 
                className={`GM__${theme}-tab-button ${sidebarContent === 'details' ? 'active' : ''}`}
                onClick={() => handleSidebarTabChange('details')}
              >
                Detalles
                {selectedVehicle && (
                  <span className={`GM__${theme}-tab-indicator`}>•</span>
                )}
              </button>
              <button 
                className={`GM__${theme}-tab-button ${sidebarContent === 'events' ? 'active' : ''}`}
                onClick={() => handleSidebarTabChange('events')}
              >
                Eventos
                {trackedEvents.length > 0 && (
                  <span className={`GM__${theme}-tab-badge`}>{trackedEvents.length}</span>
                )}
              </button>
            </div>
            
            <div className={`GM__${theme}-sidebar-content`}>
              {sidebarContent === 'details' ? (
                <VehicleDetails 
                  vehicle={selectedVehicle} 
                  onClose={clearSelection}
                  theme={theme}
                  isConnected={isConnected}
                  lastUpdate={lastUpdate}
                />
              ) : (
                <EventLog 
                  events={trackedEvents}
                  theme={theme}
                  isConnected={isConnected}
                  onClearEvents={() => {
                    // Función para limpiar eventos si es necesario
                    console.log('Limpiar eventos solicitado');
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleTracking;